import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';

const CartPage = () => {
  const { cart, updateQuantity, removeFromCart, getCartTotal, getCartCount } = useCart();
  const navigate = useNavigate();

  const subtotal = getCartTotal();
  const shipping = subtotal > 50 ? 0 : 8.99;
  const tax = subtotal * 0.0625; // 6.25% MA tax
  const total = subtotal + shipping + tax;

  if (cart.length === 0) {
    return (
      <div className="paper-texture min-h-screen flex flex-col items-center justify-center py-24">
        <ShoppingBag className="w-24 h-24 text-[#8C8885] mb-6" />
        <h1 className="font-display text-4xl mb-4" data-testid="empty-cart-title">Your Cart is Empty</h1>
        <p className="text-lg text-[#5C5855] mb-8">Start shopping to add items to your cart</p>
        <Link to="/shop">
          <Button className="btn-primary" data-testid="continue-shopping-button">
            Continue Shopping
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="paper-texture min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        <h1 className="font-display text-5xl mb-12" data-testid="cart-title">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6" data-testid="cart-items">
            {cart.map((item) => (
              <div key={item.sku} className="bg-white border border-alt-bg p-6 flex gap-6" data-testid={`cart-item-${item.sku}`}>
                {/* Product Image */}
                <div className="w-24 h-24 bg-alt-bg flex-shrink-0">
                  <img
                    src={item.imageUrl || 'https://images.unsplash.com/photo-1613463455809-28608760827d?w=200&h=200&fit=crop'}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Product Details */}
                <div className="flex-grow">
                  <Link to={`/product/${item.sku}`} className="font-display text-xl text-deep-berry hover:text-cape-red transition-colors">
                    {item.name}
                  </Link>
                  <p className="text-sm text-[#8C8885] mt-1">{item.category}</p>
                  <div className="flex items-center space-x-4 mt-4">
                    {/* Quantity Controls */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => updateQuantity(item.sku, item.quantity - 1)}
                        className="w-8 h-8 border border-alt-bg hover:border-cape-red flex items-center justify-center transition-colors"
                        data-testid={`decrease-${item.sku}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-12 text-center font-semibold" data-testid={`quantity-${item.sku}`}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.sku, item.quantity + 1)}
                        className="w-8 h-8 border border-alt-bg hover:border-cape-red flex items-center justify-center transition-colors"
                        data-testid={`increase-${item.sku}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => removeFromCart(item.sku)}
                      className="text-cape-red hover:text-[#A30000] transition-colors"
                      data-testid={`remove-${item.sku}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Price */}
                <div className="text-right">
                  <div className="price-tag text-xl" data-testid={`item-total-${item.sku}`}>
                    ${(item.retailPrice * item.quantity).toFixed(2)}
                  </div>
                  <p className="text-sm text-[#8C8885] mt-1">
                    ${item.retailPrice.toFixed(2)} each
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="bg-white border border-alt-bg p-8 soft-shadow">
              <h2 className="font-display text-2xl mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-[#2D2A26]">
                  <span>Subtotal ({getCartCount()} items)</span>
                  <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#2D2A26]">
                  <span>Shipping</span>
                  <span data-testid="cart-shipping">
                    {shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                {shipping === 0 && (
                  <p className="text-xs text-sage">Free shipping on orders over $50!</p>
                )}
                <div className="flex justify-between text-[#2D2A26]">
                  <span>Tax (MA 6.25%)</span>
                  <span data-testid="cart-tax">${tax.toFixed(2)}</span>
                </div>
                <div className="border-t border-alt-bg pt-4 flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="price-tag text-2xl" data-testid="cart-total">
                    ${total.toFixed(2)}
                  </span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/checkout')}
                className="btn-primary w-full mb-4"
                data-testid="proceed-to-checkout"
              >
                Proceed to Checkout
              </Button>

              <Link to="/shop">
                <Button className="btn-secondary w-full" data-testid="continue-shopping-cart">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
